import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, Ruler, Layers, Wallet, FileDown, Users, Globe, Sparkles,
  Check, Play, ShieldCheck, ListChecks, Wrench, ArrowLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import DemoDialog from "@/components/DemoDialog";

/**
 * SpaceCost — sister product to FlipCost, scoped to a single space (one room
 * or one project containing up to 3 related rooms).
 *
 * The full app sits behind /auth → /projects?product=spacecost. This landing
 * is the marketing entry point and lives at /spacecost.
 */
const HERO_KITCHEN =
  "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=2400&q=80";
const KITCHEN_CARD =
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1600&q=80";
const BATHROOM_CARD =
  "https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?auto=format&fit=crop&w=1600&q=80";
const GARDEN_CARD =
  "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?auto=format&fit=crop&w=1600&q=80";

const FEATURES = [
  {
    icon: Layers,
    title: "Room-by-room categories",
    body: "Demolition, joinery, plumbing, electrical, appliances, decor, labour, contingency. Add line items with qty, unit and unit cost — totals roll up automatically.",
  },
  {
    icon: Wallet,
    title: "Editable cost library",
    body: "Smart ZAR defaults you can override per project — tile flooring, vanity units, made-to-measure curtains. Reuse rates between projects.",
  },
  {
    icon: ListChecks,
    title: "Estimate vs Actual",
    body: "Toggle between budgeted and actuals as receipts come in. Variance is visible at a glance, and rolls up the room total for you.",
  },
  {
    icon: Globe,
    title: "Multi-currency",
    body: "ZAR, USD, EUR, GBP, AUD, NZD, CAD. Edit rates per project so a Cape Town bathroom and a London kitchen each stay accurate.",
  },
  {
    icon: Users,
    title: "Share with collaborators",
    body: "Invite a partner, designer or contractor — view-only or full edit. Email delivery via Resend, signed URLs, no public links.",
  },
  {
    icon: FileDown,
    title: "PDF-friendly export",
    body: "Print-ready room budget for the contractor pack, the client meeting, or your own records. One click, formatted neatly.",
  },
];

const SPACES = [
  { img: KITCHEN_CARD, alt: "Modern kitchen refit", title: "Kitchen refit", body: "Joinery + appliances + flooring", est: "R148,500" },
  { img: BATHROOM_CARD, alt: "Master bathroom remodel", title: "Bathroom remodel", body: "Tiling + plumbing + vanity", est: "R76,200" },
  { img: GARDEN_CARD, alt: "Garden landscape & deck", title: "Garden & deck", body: "Hardscape + planting + lighting", est: "R52,800" },
];

const FAQS = [
  {
    q: "How is SpaceCost different from FlipCost?",
    a: "FlipCost is built for whole-property renovations and flips — exterior, systems, soft costs, profit and feasibility. SpaceCost is laser-focused on the line-by-line cost of one space, no valuation, no flip math. Buy the tool that matches your scope.",
  },
  {
    q: "What does R180 actually unlock?",
    a: "One project containing up to 3 rooms — a kitchen + scullery + adjacent bathroom, for instance, or a primary suite. Once-off payment, no subscription. Need more rooms in the same project? Buy a +3 Room Pack for R80 any time.",
  },
  {
    q: "Can I use the same login as FlipCost?",
    a: "Yes — SpaceCost shares your MyPlanCost account. Log in once and switch between FlipCost and SpaceCost projects from your dashboard.",
  },
  {
    q: "Are my numbers private?",
    a: "Always. Projects are private to your account by default. Sharing is opt-in per project, with a clear view-only or edit role for each invitee.",
  },
];

export default function SpaceCost() {
  const navigate = useNavigate();
  const [demoOpen, setDemoOpen] = useState(false);

  useEffect(() => {
    document.title = "SpaceCost — Cost a single space, before you swing the first hammer";
    return () => { document.title = "MyPlanCost"; };
  }, []);

  return (
    <div
      className="min-h-screen bg-[#FAFAF9] text-[#1C1C1C] fc-fade-in"
      data-testid="spacecost-landing"
      data-theme="spacecost"
    >
      <header className="sticky top-0 z-30 fc-header-bar">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <button
              data-testid="spacecost-back-myplancost"
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-1.5 text-[12px] text-[#57534E] hover:text-[#1C1C1C] bg-white hover:bg-black/[0.03] border border-black/[0.08] rounded-full h-8 px-3 transition-colors shrink-0"
              aria-label="Back to MyPlanCost"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> <span className="hidden sm:inline">MyPlanCost</span>
            </button>
            <button
              data-testid="spacecost-home-btn"
              onClick={() => navigate("/spacecost")}
              className="flex items-baseline gap-2.5 sm:gap-3 group shrink-0"
              aria-label="SpaceCost by MyPlanCost — home"
            >
              <span className="fc-heading text-2xl sm:text-[28px] font-extrabold tracking-tight text-[#1C1C1C] leading-none">SpaceCost</span>
              <span className="hidden sm:inline text-[10px] uppercase tracking-[0.25em] text-[#A8A29E] font-medium leading-none whitespace-nowrap">
                by MyPlanCost
              </span>
            </button>
          </div>
          <nav className="hidden md:flex items-center gap-1 text-[13px] text-[#57534E]">
            <button data-testid="nav-features" onClick={() => document.getElementById("features")?.scrollIntoView({ behavior: "smooth" })} className="px-3 py-2 hover:text-[#1C1C1C]">Features</button>
            <button data-testid="nav-pricing" onClick={() => navigate("/spacecost/pricing")} className="px-3 py-2 hover:text-[#1C1C1C]">Pricing</button>
            <button data-testid="nav-how-it-works" onClick={() => navigate("/spacecost/how-it-works")} className="px-3 py-2 hover:text-[#1C1C1C]">How it works</button>
            <button data-testid="nav-faq" onClick={() => navigate("/spacecost/faq")} className="px-3 py-2 hover:text-[#1C1C1C]">FAQ</button>
            <Button
              data-testid="spacecost-cta-signin"
              onClick={() => navigate("/auth?mode=login&product=spacecost")}
              variant="ghost"
              className="text-[#57534E] hover:text-[#1C1C1C] h-9 ml-1"
            >
              Sign in
            </Button>
          </nav>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="spacecost-cross-flipcost"
              onClick={() => navigate("/flipcost")}
              className="hidden sm:inline-flex items-center gap-1.5 text-[12px] text-sage-700 hover:text-sage-800 bg-sage-50 hover:bg-sage-100 border border-sage-200 rounded-full h-8 px-3 transition-colors"
              title="MyPlanCost umbrella · whole house? Try FlipCost"
            >
              Whole house? Try FlipCost <ArrowRight className="w-3 h-3" />
            </button>
            {/* Mobile-only Sign in (the nav-bar Sign in is desktop-only). */}
            <Button
              data-testid="spacecost-cta-signin-mobile"
              onClick={() => navigate("/auth?mode=login&product=spacecost")}
              variant="ghost"
              className="md:hidden text-[#57534E] hover:text-[#1C1C1C] h-9"
            >
              Sign in
            </Button>
            <Button
              data-testid="spacecost-cta-register"
              onClick={() => navigate("/auth?mode=register&product=spacecost")}
              className="bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] h-9 px-3 text-sm rounded-lg"
            >
              Start a project <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-sage-700 bg-sage-100 border border-sage-200 rounded-full px-3 py-1">
              <Sparkles className="w-3 h-3" /> MyPlanCost · Cost planning suite
            </div>
            <h1 className="fc-heading text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight mt-5 leading-[1.05]">
              Renovate with confidence —<br /> every cost, every room.
            </h1>
            <p className="text-base sm:text-lg text-[#57534E] mt-6 max-w-xl leading-relaxed">
              SpaceCost is a room-by-room renovation budgeting tool that helps you forecast costs
              with clarity — breaking your project into structured categories and line items so you
              can plan accurately and stay in control.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button
                data-testid="hero-cta-register"
                onClick={() => navigate("/auth?mode=register&product=spacecost")}
                className="bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] rounded-lg h-12 px-6 text-[15px] font-medium"
              >
                Start your first project <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
              <Button
                data-testid="hero-cta-howitworks"
                onClick={() => navigate("/spacecost/how-it-works")}
                variant="ghost"
                className="text-[#57534E] hover:text-[#1C1C1C] h-12 px-4"
              >
                See how it works
              </Button>
              <Button
                data-testid="hero-cta-demo"
                onClick={() => setDemoOpen(true)}
                variant="ghost"
                className="text-sage-700 hover:text-sage-800 h-12 px-4 inline-flex items-center gap-1.5"
              >
                <Play className="w-4 h-4" /> Live demo
              </Button>
            </div>
            <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-[12px] text-[#78716C]">
              <span className="inline-flex items-center gap-1.5"><Wallet className="w-3.5 h-3.5 text-sage-700" /> R180 ZAR once-off</span>
              <span className="inline-flex items-center gap-1.5"><Layers className="w-3.5 h-3.5 text-sage-700" /> Up to 3 rooms per project</span>
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-sage-700" /> No subscription</span>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-6 bg-gradient-to-br from-sage-100/70 via-transparent to-sage-200/40 rounded-3xl blur-2xl" />
            <div className="relative fc-card p-1.5 overflow-hidden">
              <img
                src={HERO_KITCHEN}
                alt="Architectural blueprint fading into a modern kitchen"
                className="w-full h-[420px] object-cover rounded-xl"
              />
            </div>
            <div className="absolute -bottom-5 -left-4 sm:left-6 fc-card p-4 sm:p-5 max-w-[260px]">
              <div className="fc-label">Sample budget</div>
              <div className="fc-heading text-2xl text-[#1C1C1C] mt-1">R148,500</div>
              <div className="text-[12px] text-[#78716C] mt-1">Kitchen refit · 9 categories · 23 line items</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-16 sm:py-24 bg-white border-y border-black/[0.05]">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          <div className="max-w-2xl">
            <div className="fc-label text-sage-700">Tools that earn their keep</div>
            <h2 className="fc-heading text-3xl sm:text-4xl font-medium tracking-tight mt-3 leading-[1.1]">
              Everything you need to cost one space — properly.
            </h2>
            <p className="text-base text-[#57534E] mt-4 leading-relaxed">
              Not feasibility. Not valuations. Just the line-by-line numbers for the room you're actually building.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="features-grid">
            {FEATURES.map((f) => (
              <div key={f.title} className="fc-card p-6 hover:border-sage-300/60 transition-colors">
                <div className="w-10 h-10 rounded-lg bg-sage-100 border border-sage-200 grid place-items-center text-sage-700">
                  <f.icon className="w-4 h-4" />
                </div>
                <div className="fc-heading text-[15px] font-medium text-[#1C1C1C] mt-4">{f.title}</div>
                <div className="text-[13px] text-[#57534E] mt-2 leading-relaxed">{f.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Spaces */}
      <section className="py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-end">
            <div className="lg:col-span-2">
              <div className="fc-label text-sage-700">Built for one space</div>
              <h2 className="fc-heading text-3xl sm:text-4xl font-medium tracking-tight mt-3 leading-[1.1]">
                Kitchen, bathroom, garden, office, nursery — your call.
              </h2>
              <p className="text-base text-[#57534E] mt-4 leading-relaxed">
                SpaceCost is scoped to <strong>one project per purchase</strong>. That project can hold up to 3 rooms — a
                kitchen + scullery + adjacent bathroom, for instance — so you can cost the whole zone you're
                actually building.
              </p>
              <div className="mt-6">
                <Button
                  data-testid="spaces-cta-pricing"
                  onClick={() => navigate("/spacecost/pricing")}
                  className="bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] rounded-lg h-11 px-5"
                >
                  See pricing <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </div>
            </div>
            <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="spaces-grid">
              {SPACES.map((s) => (
                <div key={s.title} className="fc-card overflow-hidden">
                  <img src={s.img} alt={s.alt} className="w-full h-44 object-cover" />
                  <div className="p-4">
                    <div className="fc-label text-sage-700">{s.body}</div>
                    <div className="fc-heading text-[14px] text-[#1C1C1C] mt-1.5">{s.title}</div>
                    <div className="text-[12px] text-[#78716C] mt-1">Estimated: {s.est}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mini-FAQ */}
      <section className="py-16 sm:py-20 bg-white border-y border-black/[0.05]">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <div className="fc-label text-sage-700">Quick questions</div>
          <h2 className="fc-heading text-3xl font-medium tracking-tight mt-3">Before you start</h2>
          <div className="mt-8 space-y-4" data-testid="landing-faq">
            {FAQS.map((f) => (
              <details key={f.q} className="fc-card p-5 group">
                <summary className="flex items-center justify-between gap-3 cursor-pointer list-none">
                  <span className="fc-heading text-[14px] text-[#1C1C1C]">{f.q}</span>
                  <span className="text-sage-700 text-xs uppercase tracking-[0.2em]">read</span>
                </summary>
                <p className="text-[13px] text-[#57534E] leading-relaxed mt-3">{f.a}</p>
              </details>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Button
              data-testid="landing-faq-more"
              onClick={() => navigate("/spacecost/faq")}
              variant="ghost"
              className="text-sage-700 hover:text-sage-800"
            >
              All FAQs <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 text-center">
          <div className="fc-label text-sage-700">R180 ZAR · once-off</div>
          <h2 className="fc-heading text-4xl sm:text-5xl font-medium tracking-tight mt-3 leading-[1.05]">
            Stop guessing. Start costing.
          </h2>
          <p className="text-base text-[#57534E] mt-5 max-w-xl mx-auto leading-relaxed">
            One single payment unlocks one project — up to 3 rooms — with the full toolkit. No subscriptions. Cancel nothing.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Button
              data-testid="cta-bottom-register"
              onClick={() => navigate("/auth?mode=register&product=spacecost")}
              className="bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] rounded-lg h-12 px-6 text-[15px] font-medium"
            >
              Get started <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
            <Button
              data-testid="cta-bottom-pricing"
              onClick={() => navigate("/spacecost/pricing")}
              variant="ghost"
              className="text-[#57534E] hover:text-[#1C1C1C] h-12 px-4"
            >
              View pricing
            </Button>
          </div>
        </div>
      </section>

      <footer className="bg-[#1C1C1C] text-white/70 mt-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-12 grid grid-cols-2 md:grid-cols-4 gap-8 text-[13px]">
          <div className="col-span-2">
            <div className="fc-heading text-white text-base">SpaceCost</div>
            <div className="text-[12px] mt-2 text-white/55 max-w-sm leading-relaxed">
              Room-level cost forecasting from the MyPlanCost suite. SpaceCost helps homeowners and pros budget a single space, properly.
            </div>
            <button
              data-testid="footer-back-flipcost"
              onClick={() => navigate("/flipcost")}
              className="inline-flex items-center gap-1.5 mt-4 text-sage-300 hover:text-white text-[12px]"
            >
              <ArrowLeft className="w-3 h-3" /> Whole house? Try FlipCost
            </button>
          </div>
          <div>
            <div className="text-white text-[12px] uppercase tracking-[0.18em]">Product</div>
            <ul className="mt-4 space-y-2.5">
              <li><button data-testid="footer-features" onClick={() => document.getElementById("features")?.scrollIntoView({ behavior: "smooth" })} className="hover:text-white">Features</button></li>
              <li><button data-testid="footer-pricing" onClick={() => navigate("/spacecost/pricing")} className="hover:text-white">Pricing</button></li>
              <li><button data-testid="footer-howitworks" onClick={() => navigate("/spacecost/how-it-works")} className="hover:text-white">How it works</button></li>
              <li><button data-testid="footer-faq" onClick={() => navigate("/spacecost/faq")} className="hover:text-white">FAQ</button></li>
            </ul>
          </div>
          <div>
            <div className="text-white text-[12px] uppercase tracking-[0.18em]">Suite</div>
            <ul className="mt-4 space-y-2.5">
              <li><button data-testid="footer-myplancost" onClick={() => navigate("/")} className="hover:text-white">MyPlanCost</button></li>
              <li><button data-testid="footer-flipcost" onClick={() => navigate("/flipcost")} className="hover:text-white">FlipCost</button></li>
              <li><button data-testid="footer-disclaimer" onClick={() => navigate("/disclaimer")} className="hover:text-white">Disclaimer</button></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-6xl mx-auto px-4 sm:px-8 py-5 flex flex-wrap items-center justify-between gap-3 text-[12px] text-white/45">
            <div>© {new Date().getFullYear()} MyPlanCost. All rights reserved.</div>
            <div>Estimates only. See the <button onClick={() => navigate("/disclaimer")} className="underline hover:text-white/80">full disclaimer</button>.</div>
          </div>
        </div>
      </footer>

      <DemoDialog open={demoOpen} onOpenChange={setDemoOpen} product="spacecost" />
    </div>
  );
}
